import type { SxProps, Theme } from "@mui/material";
import { ListItem } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useRef } from "react";
import type { DragSourceMonitor } from "react-dnd";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";

import type { DraggableMetaType, MetaDescriptorBase } from "../../../../../../../../api/types/svTypes.ts";
import FieldTreeAddButton from "./FieldTreeAddButton.tsx";

interface Props<T extends MetaDescriptorBase> {
  field: T;
  sx?: SxProps<Theme>;
  onDragStart?: () => void;
  disabled?: boolean;
  active?: boolean;
  addableField?: DraggableMetaType;
}

const FieldItemContainer = <T extends MetaDescriptorBase>({
  field,
  sx,
  children,
  onDragStart,
  disabled,
  active,
  addableField,
}: PropsWithChildren<Props<T>>) => {
  const onDragStartRef = useRef(onDragStart);
  useEffect(() => {
    onDragStartRef.current = onDragStart;
  }, [onDragStart]);

  const collect = useCallback(
    (monitor: DragSourceMonitor<T, unknown>) => ({
      isDragging: monitor.isDragging(),
    }),
    []
  );

  const [{ isDragging }, drag, dragPreview] = useDrag(
    () => ({
      type: field.itemType,
      item: field,
      collect,
      canDrag: () => !disabled,
    }),
    [field, disabled]
  );

  useEffect(() => {
    dragPreview(getEmptyImage());
  }, [dragPreview]);

  useEffect(() => {
    if (isDragging) {
      onDragStartRef.current?.();
    }
  }, [isDragging]);

  return (
    <ListItem
      role="Handle"
      ref={(node) => {
        drag(node);
      }}
      className={active ? "is-active" : undefined}
      sx={{
        p: "4px 6px",
        "& .row-actions": { visibility: "hidden" },
        "& .add-field-action": { opacity: 0 },
        "&:hover .add-field-action, &:focus-within .add-field-action": { opacity: 1 },
        "&:hover, &.is-active": {
          cursor: "pointer",
          backgroundColor: "action.hover",
        },
        "&:hover .row-actions, &:focus-within .row-actions, &.is-active .row-actions": { visibility: "visible" },
        "&:hover .meta-icon, &.is-active .meta-icon": { display: "none" },
        "&:hover .drag-icon, &.is-active .drag-icon": { display: "inline-flex" },
        opacity: isDragging || disabled ? 0.5 : 1,
        ...sx,
      }}
    >
      {children}
      {!disabled && addableField && <FieldTreeAddButton field={addableField} />}
    </ListItem>
  );
};

export default FieldItemContainer;
